import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { User } from '@/modules/ta/user/models/User';
import { ReportStudioTemplateMetadata } from '@/modules/ta/reportstudio/models/ReportStudioTemplateMetadata';
import {
  FontSize,
  PageAspectRatio,
  PageFormat,
  PageOrientation,
  ReportTemplateType,
  PageSpecifications,
} from '@/modules/ta/reportstudio/reportstudio.constants';
import { ReportStudioPage } from '@/modules/ta/reportstudio/models/ReportStudioPage';
import { ReportStudioElementStylesUIFactory } from '@/modules/ta/reportstudio/factories/ReportStudioElementStylesUIFactory';
import { getters } from '@/modules/core/app/helpers/store';
import { isEmpty } from 'lodash';

export class ReportStudioTemplate extends BaseEntityModel {
  /**
   * @var {string}
   */
  id;

  /**
   * @var {string}
   */
  title;

  /**
   * @var {string}
   */
  orientation;

  /**
   * @var {string}
   */
  format;

  /**
   * @var {string}
   */
  thumbnail;

  /**
   * @var {object}
   */
  metadata;

  /**
   * @var {boolean}
   */
  is_preview;

  /**
   * @var {boolean}
   */
  is_private;

  /**
   * @var {boolean}
   */
  is_predefined;

  /**
   * @var {boolean}
   */
  is_favorite;

  /**
   * @var {boolean}
   */
  can_favorite;

  /**
   * @var {string}
   */
  type;

  /**
   * @var {number}
   */
  number_of_pages;

  /**
   * @var {datetime}
   */
  created_at;

  /**
   * @var {string}
   */
  formatted_created_at;

  /**
   * @var {datetime}
   */
  updated_at;

  /**
   * @var {string}
   */
  formatted_updated_at;

  /**
   * @var {?string}
   */
  user_id;

  /**
   * @var {object}
   */
  user;

  /**
   * @var {string}
   */
  user_image_metadata;

  /**
   * @var {Array.<*>}
   */
  data_sources;

  /**
   * @var {string}
   */
  cluster;

  /**
   * @var {string}
   */
  cluster_id;

  /**
   * @var {string}
   */
  cluster_name;

  /**
   * @var {string}
   */
  client;

  /**
   * @var {string}
   */
  client_id;

  /**
   * @var {string}
   */
  client_name;

  /**
   * @var {boolean}
   */
  is_template;

  /**
   * @var {string}
   */
  type_display;

  /**
   * @var {string}
   */
  orientation_display;

  /**
   * @var {boolean}
   */
  is_in_library;

  /*
   * @var {number}
   */
  library_download_count;

  /**
   * @var {string}
   */
  client_group_id;

  /**
   * @var {string}
   */
  client_group_name;

  /**
   * @var {Object}
   */
  client_group;

  /**
   * @var {string}
   */
  user_type;

  /**
   * @var {string}
   */
  client_logo_url;

  /**
   * @var {string}
   */
  format_display;

  /**
   * @var {string}
   */
  report_language;

  /**
   * @var {object}
   */
  global_filters;

  /**
   * @var {array}
   */
  pages;

  /**
   * @var {array}
   */
  elements;

  /**
   * @var {string}
   */
  logo_url;

  /**
   * @var {array}
   */
  connected_service_ids;

  /**
   * @var {string}
   */
  timegroup;

  /**
   * @var {boolean}
   */
  has_granular_permissions;

  /**
   * @var {object}
   */
  granular_access_levels;

  /**
   * @var {boolean}
   */
  is_locked;

  /**
   * @var {array}
   */
  reporting_profile_ids;

  /**
   * @var {string}
   */
  report_language_display;

  constructor(model = {}) {
    super(model);

    this.id = model.id;
    this.title = model.title;
    this.orientation = model.orientation;
    this.format = model.format ?? PageFormat.STANDARD;
    this.thumbnail = model.thumbnail;
    this.metadata = model.metadata ? new ReportStudioTemplateMetadata(model.metadata) : {};
    this.is_preview = model.is_preview;
    this.is_private = model.is_private;
    this.is_predefined = model.is_predefined;
    this.is_favorite = model.is_favorite;
    this.can_favorite = model.can_favorite;
    this.type = model.type;
    this.number_of_pages = model.number_of_pages;
    this.created_at = model.created_at;
    this.formatted_created_at = model.formatted_created_at;
    this.updated_at = model.updated_at;
    this.formatted_updated_at = model.formatted_updated_at;
    this.user_id = model.user_id;
    this.user = model.user ? new User(model.user) : null;
    this.user_image_metadata = model.user_image_metadata;
    this.data_sources = model.data_sources;
    this.cluster = model.cluster;
    this.cluster_id = model.cluster_id;
    this.cluster_name = model.cluster_name;
    this.client = model.client;
    this.client_id = model.client_id;
    this.client_name = model.client_name;
    this.is_template = model.is_template;
    this.type_display = model.type_display;
    this.orientation_display = model.orientation_display;
    this.is_in_library = model.is_in_library;
    this.library_download_count = model.library_download_count;
    this.tags = model.tags;
    this.client_group_id = model.client_group_id;
    this.client_group_name = model.client_group_name;
    this.client_group = model.client_group;
    this.user_type = model.user_type;
    this.client_logo_url = model.client_logo_url;
    this.format_display = model.format_display;
    this.report_language = model.report_language ?? null;
    this.global_filters = model.global_filters ?? {};

    this.can_be_copied = model.can_be_copied ?? false;
    this.can_be_deleted = model.can_be_deleted ?? false;
    this.can_be_edited = model.can_be_edited ?? false;

    this.pages = (model.pages ?? []).map((page) => new ReportStudioPage(page));
    this.elements = model.elements || [];
    this.logo_url = model.logo_url;
    this.connected_service_ids = model.connected_service_ids ?? [];
    this.timegroup = model.timegroup;
    this.report_has_elements = model.report_has_elements ?? true;
    this.has_granular_permissions = model.has_granular_permissions;
    this.granular_access_levels = model.granular_access_levels || {};
    this.is_locked = model.is_locked;
    this.reporting_profile_ids = model.reporting_profile_ids ?? [];
    this.report_language_display = model.report_language_display ?? '';
  }

  /**
   *
   * @returns {boolean}
   */
  isTypeProposal() {
    return this.type === ReportTemplateType.TYPE_PROPOSAL;
  }

  /**
   *
   * @returns {boolean}
   */
  isOrientationLandscape() {
    return this.orientation === PageOrientation.TYPE_LANDSCAPE;
  }

  /**
   *
   * @param key
   * @param value
   */
  setDrawOption(key, value) {
    this.metadata.draw_options[key].value = value;
    this.metadata.draw_options[key].overridden = !value;
  }

  /**
   *
   * @returns {*}
   */
  getRandomThemeColor() {
    const value = this.metadata.chart_palette.length;
    const index = Math.floor(Math.random() * value);
    return this.metadata.chart_palette[index];
  }

  getPageById(pageId) {
    return this.pages.find((searchPage) => searchPage.id === pageId);
  }

  /**
   *
   * @returns {number}
   */
  getPageHeight() {
    return this.isOrientationLandscape()
      ? PageSpecifications.WIDTH / this.aspectRatio()
      : PageSpecifications.WIDTH;
  }

  /**
   *
   * @returns {number}
   */
  getPageWidth() {
    return this.isOrientationLandscape()
      ? PageSpecifications.WIDTH
      : PageSpecifications.WIDTH / this.aspectRatio();
  }

  /**
   * @returns {boolean}
   */
  isFontSizeSmall() {
    return this.metadata.font_size === FontSize.SIZE_SMALL;
  }

  /**
   *
   * @returns {boolean}
   */
  isFontSizeMedium() {
    return this.metadata.font_size === FontSize.SIZE_MEDIUM;
  }

  /**
   *
   * @returns {boolean}
   */
  isFontSizeLarge() {
    return this.metadata.font_size === FontSize.SIZE_LARGE;
  }

  /**
   *
   * @returns {*}
   */
  aspectRatio() {
    return this.format === PageFormat.WIDESCREEN
      ? PageAspectRatio.WIDESCREEN
      : PageAspectRatio.STANDARD;
  }

  /**
   * @returns {boolean}
   */
  canEdit() {
    return this.can_be_edited;
  }

  /**
   * @returns {boolean}
   */
  hasGranularEditAccess() {
    if (!this.has_granular_permissions) return true;

    return this.granular_access_levels.can_be_edited;
  }

  getElementDimensions(element) {
    return element.snap_to_grid
      ? [
          ReportStudioElementStylesUIFactory.getPercentToPixelValue(
            element.width,
            this.getPageWidth()
          ),
          ReportStudioElementStylesUIFactory.getPercentToPixelValue(
            element.height,
            this.getPageHeight()
          ),
        ]
      : [element.width, element.height];
  }

  getElementCoordinates(element) {
    return element.snap_to_grid
      ? [
          ReportStudioElementStylesUIFactory.getPercentToPixelValue(
            element.x_position,
            this.getPageWidth()
          ),
          ReportStudioElementStylesUIFactory.getPercentToPixelValue(
            element.y_position,
            this.getPageHeight()
          ),
        ]
      : [element.x_position, element.y_position];
  }

  convertPixelValuesToPercentValues([x, y]) {
    return [
      ReportStudioElementStylesUIFactory.getPixelToPercentValue(x, this.getPageWidth()),
      ReportStudioElementStylesUIFactory.getPixelToPercentValue(y, this.getPageHeight()),
    ];
  }

  hasConnectedDataSource() {
    const filterByDataSourceId = this.data_sources?.filter(
      ({ data_source_id }) => !isEmpty(data_source_id)
    );
    if (isEmpty(filterByDataSourceId)) return true;

    const connectedServiceIds = getters.service.getConnectedServices().map(({ id }) => id);
    return filterByDataSourceId?.some((dataSource) =>
      connectedServiceIds.includes(dataSource.data_source_id)
    );
  }

  anyPageHasElements() {
    return this.pages.filter((page) => page.hasElements()).length > 0;
  }

  /**
   * @returns {boolean}
   */
  isDemoModeEnabled() {
    return this.metadata.enable_demo_mode;
  }
}
